import { z } from 'zod'
import {
  IMPOTS,
  MODES_REGLEMENT,
  REGLEMENTS_PIECE,
  RUBRIQUES,
  type ModeReglement,
  type ModeTransmission,
  type MoyenPaiement,
  type RoleTiers,
  type SensPaiement,
  type StatutEcheance,
  type StatutPaiement,
  TYPES_PIECE,
  type TypePiece,
} from './domaine'
import {
  appliquerConsignesFournisseur,
  type ConsigneFournisseurRef,
  consigneDepuisPiece,
  referencePiece,
} from './consignes-fournisseur'
import { type BienRef, bienDesReferences } from './biens'
import { normaliserLibelle } from './libelle'
import { extractionSante } from './sante'

// Pièces entrantes : ce que l'extraction en retient, et la décision de rattachement
// (entité, tiers, effets). Fonctions pures ; les services appliquent les effets.

const texteOuNul = z.string().nullable()
const centimesOuNul = z.number().int().nullable()
const jourOuNul = z.iso.date().nullable()

/** Résultat d'une extraction (Factur-X, UBL ou Claude), montants en centimes. */
export const extractionPiece = z.object({
  type: z.enum(TYPES_PIECE),
  /** Facture reçue d'un fournisseur ou émise par l'entité ; null si ce n'est pas une facture. */
  sens: z.enum(['recue', 'emise']).nullable(),
  emetteur: z.object({ nom: texteOuNul, siren: texteOuNul, iban: texteOuNul }),
  destinataire: z.object({ nom: texteOuNul, siren: texteOuNul }),
  numero: texteOuNul,
  dateDocument: jourOuNul,
  dateEcheance: jourOuNul,
  montants: z.object({
    htCts: centimesOuNul,
    tvaCts: centimesOuNul,
    ttcCts: centimesOuNul,
    /** Reste à payer (net des acomptes), ou montant d'un avis d'imposition. */
    aPayerCts: centimesOuNul,
  }),
  devise: texteOuNul,
  /** Objet court : « Hébergement octobre 2026 », « CFE 2026 ». */
  objet: texteOuNul,
  /** Pour un avis ou un accusé : l'impôt concerné et son année. */
  impot: z.enum(IMPOTS).nullable(),
  annee: z.number().int().nullable(),
  modeReglement: z.enum(MODES_REGLEMENT),
  /**
   * Le document demande-t-il une action du destinataire (répondre, envoyer, signer) ?
   * Null ou absent : on ne sait pas (un XML structuré ne le dit pas).
   */
  actionRequise: z.boolean().nullable().optional(),
  /** Confiance de 0 à 1 (1 pour un XML structuré). */
  confiance: z.number().min(0).max(1),
  /** Corrigée à la main sur la fiche : ce qui est affiché a été relu par un humain. */
  corrigee: z.boolean().optional(),
  /**
   * Entité du référentiel que désigne l'indication du déposant (« perso », « pour Flow
   * Design »), lue par Claude avec la pièce ; null si elle n'en désigne aucune.
   */
  entiteIndiquee: texteOuNul.optional(),
  /**
   * Rubrique du plan de classement reconnue à la lecture (achats, fiscal, juridique…) ; null :
   * la lecture hésite, un humain choisit ; absente : XML structuré ou lecture antérieure au plan.
   */
  rubrique: z.enum(RUBRIQUES).nullable().optional(),
  /** Exercice ou année que la pièce concerne quand ce n'est pas celui de sa date (liasse 2025). */
  exerciceConcerne: z.number().int().min(2000).max(2100).nullable().optional(),
  /**
   * Pièce de santé du foyer (décompte de l'Assurance maladie ou d'une mutuelle, facture d'un
   * praticien, ordonnance) : ses lignes de soins ; null ou absente pour toute autre pièce.
   */
  sante: extractionSante.nullable().optional(),
  /**
   * Références qui désignent l'endroit ou l'abonnement (PRM ou PDL, PCE, contrat, client,
   * ligne, adresse du point de livraison ou du bien concerné) : elles départagent les
   * consignes d'un fournisseur à plusieurs endroits. Absentes si la pièce n'en porte pas.
   */
  references: z.array(referencePiece).max(20).optional(),
  /**
   * Facture qu'une plateforme émet au nom et pour le compte d'un vendeur (mandat de
   * facturation : chauffeur d'une course VTC, vendeur d'une place de marché) : l'émetteur est
   * la plateforme, qu'on paie et que montre le relevé ; le vendeur est noté ici. Null ou absent
   * pour toute autre pièce.
   */
  vendeur: z.object({ nom: texteOuNul, siren: texteOuNul }).nullable().optional(),
  /**
   * Facture d'un vendeur tiers sur une place de marché (Amazon, décision de Pierre-Marie du
   * 30/10/2026) : l'émetteur est le vendeur, et la plateforme, qu'on paie et que montre le relevé,
   * est notée ici (`vendeurEnEmetteur`). Null ou absent pour toute autre pièce.
   */
  plateforme: z
    .object({ nom: texteOuNul, siren: texteOuNul, iban: texteOuNul })
    .nullable()
    .optional(),
})
export type ExtractionPiece = z.infer<typeof extractionPiece>

export type EntiteRef = {
  id: string
  nom: string
  siren: string | null
  archivee: boolean
  transmission: ModeTransmission
  adresseCollecte: string | null
  /**
   * Autres façons dont les pièces la désignent : SIRET, TVA intracommunautaire, anciens noms,
   * nom commercial, adresse mail. Un identifiant d'au moins neuf chiffres vaut SIREN.
   */
  identifiants?: readonly string[]
}

export type TiersRef = {
  id: string
  nom: string
  siren: string | null
  iban: string | null
  libellesBancaires: readonly string[]
  entiteParDefautId: string | null
}

export type EcheanceRef = {
  id: string
  entiteId: string
  /** Type de la règle (cfe, taxe_fonciere, tva_ca3…). */
  regleType: string | null
  periode: string
  date: string
  statut: StatutEcheance
  montantPrevuCts: number | null
  /** Montant constaté quand l'échéance a été faite. */
  montantReelCts?: number | null
}

export type PaiementRef = {
  id: string
  entiteId: string
  tiersId: string | null
  sens: SensPaiement
  montantDuCts: number
  statut: StatutPaiement
  pieceId: string | null
  /** Numéro de la pièce liée : une facture et son reçu ne font qu'un paiement. */
  pieceNumero?: string | null
}

export type EffetPiece =
  | { type: 'lier_paiement'; paiementId: string }
  /** La pièce atteste le règlement : lier le paiement s'il ne l'est pas, le passer payé. */
  | { type: 'acquitter_paiement'; paiementId: string }
  | {
      type: 'creer_paiement'
      sens: SensPaiement
      montantDuCts: number
      devise: string
      datePrevue: string | null
      moyen: MoyenPaiement | null
      libelle: string | null
      /** Déjà réglé d'après la pièce : créé payé, la banque confirmera. */
      dejaRegle: boolean
    }
  | { type: 'fixer_montant'; echeanceId: string; montantCts: number }
  | { type: 'preuve'; echeanceId: string }
  /** Avis d'une échéance déjà faite, au même montant : la pièce la documente, rien d'autre. */
  | { type: 'joindre_echeance'; echeanceId: string }
  | { type: 'creer_tache'; titre: string; dateDue: string | null }

export type TiersDecision =
  { tiersId: string } | { creer: { nom: string; siren: string | null; role: RoleTiers } } | null

export type Rattachement =
  | {
      issue: 'a_verifier'
      entiteId: string | null
      motifs: string[]
      /** Consignes du fournisseur dont le texte reste à soumettre à Claude, avant de demander. */
      consignesAJuger?: string[]
    }
  | {
      issue: 'rattachee'
      entiteId: string
      tiers: TiersDecision
      effets: EffetPiece[]
      /** Consigne du fournisseur qui a tranché l'entité. */
      consigneFournisseurId?: string
      /** Bien du référentiel dont l'adresse, lue sur la pièce, a tranché l'entité. */
      bienId?: string
    }

export const SEUIL_CONFIANCE = 0.6

/** Règles d'échéance concernées par un impôt. */
const REGLES_IMPOT: Record<string, readonly string[]> = {
  cfe: ['cfe', 'cfe_1447c'],
  taxe_fonciere: ['taxe_fonciere'],
  is: ['is_solde', 'is_acompte'],
  tva: ['tva_ca3'],
  ir: ['declaration_revenus'],
}

const MOYEN_DU_REGLEMENT: Record<ModeReglement, MoyenPaiement | null> = {
  prelevement: 'prelevement',
  virement: 'virement_emis',
  carte: 'carte',
  cheque: 'cheque',
  deja_paye: 'autre',
  inconnu: null,
}

const PAIEMENTS_OUVERTS: readonly StatutPaiement[] = ['prevu', 'programme', 'partiel']

/**
 * Montant effectif d'une pièce, ce qui est débité ou remboursé : le montant à payer lu s'il est
 * non nul (total après remise, frais ou solde antérieur), sinon le TTC. Un à payer nul est le
 * reste dû d'une facture déjà réglée (carte) : c'est alors le TTC qui a été payé. Signé (un avoir
 * est négatif). Règle unique : lecture de la pièce (`montantDeLaPiece`), paiement d'une facture
 * déjà réglée (création et correction), factures d'une commande de portail (`montantFacture`).
 */
export function montantEffectif(m: {
  ttcCts: number | null
  aPayerCts: number | null
}): number | null {
  return m.aPayerCts ? m.aPayerCts : (m.ttcCts ?? m.aPayerCts ?? null)
}

/**
 * L'entité au nom de laquelle une facture reçue est écrite, si c'est une des nôtres. Le nom de
 * facturation d'un portail ne décide pas de l'entité d'une pièce (`INDICATION_AMAZON`), mais une
 * facture au nom d'une autre société ne justifie pas forcément sa dépense : c'est à signaler.
 */
export function entiteAuNomDeLaFacture<E extends EntiteRef>(
  x: Pick<ExtractionPiece, 'type' | 'sens' | 'destinataire'> | null,
  entites: readonly E[],
): E | null {
  if (!x || x.type !== 'facture' || x.sens === 'emise') return null
  return trouverEntite(x.destinataire, entites) as E | null
}

/** Ce que dit la décision « qui a payé » quand la dépense va ailleurs ; null : rien à signaler. */
export function alerteNomFacture(e: {
  nomSurLaFacture: { entiteId: string; nom: string } | null
  charge: { entiteId: string; nom: string } | null
}): string | null {
  if (!e.nomSurLaFacture || !e.charge || e.nomSurLaFacture.entiteId === e.charge.entiteId)
    return null
  return `Cette facture est au nom de ${e.nomSurLaFacture.nom}, pas de ${e.charge.nom} : elle ne justifie peut-être pas sa dépense. À voir avec son cabinet ; sur le site, choisir la bonne adresse de facturation à la commande.`
}

/**
 * Place de marché : la lecture nomme la plateforme en émetteur et le vendeur à part ; sur la
 * pièce, l'émetteur est le vendeur, et la plateforme reste notée pour le règlement.
 */
export function vendeurEnEmetteur(x: ExtractionPiece): ExtractionPiece {
  if (!x.vendeur?.nom) return x
  return {
    ...x,
    emetteur: { nom: x.vendeur.nom, siren: x.vendeur.siren, iban: null },
    plateforme: x.emetteur,
    vendeur: null,
  }
}

/**
 * Ce que le rattachement voit pour décider de l'entité et du paiement : celui qu'on paie (la
 * plateforme, s'il y en a une) et ce qu'on lui paie (le total du fichier, s'il regroupe plusieurs
 * factures). Le paiement, le relevé et le cabinet ne connaissent que cela.
 */
export function extractionDuReglement(
  x: ExtractionPiece,
  total: {
    ht?: number | null
    tva?: number | null
    ttc?: number | null
    aPayer?: number | null
  } | null,
): ExtractionPiece {
  return {
    ...x,
    ...(x.plateforme && { emetteur: x.plateforme }),
    ...(total && {
      montants: {
        htCts: total.ht ?? null,
        tvaCts: total.tva ?? null,
        ttcCts: total.ttc ?? null,
        aPayerCts: total.aPayer ?? null,
      },
    }),
  }
}

/**
 * Le montant qu'on lit sur une pièce (description, placement dans le plan) : son montant
 * effectif ; une facture déjà réglée par carte ne se lit pas « 0 € ».
 */
export function montantDeLaPiece(
  m: { ttc?: number | null; aPayer?: number | null } | null | undefined,
): number | null {
  if (!m) return null
  return montantEffectif({ ttcCts: m.ttc ?? null, aPayerCts: m.aPayer ?? null })
}

/**
 * Facture reçue déjà réglée d'après la pièce : reçu ou mention « payée », carte débitée
 * (abonnement), prélèvement (le fournisseur se sert : rien à suivre, décision de Pierre-Marie
 * du 02/10/2026), ou reste dû nul sur un total positif. Seuls le chèque, le virement et
 * l'inconnu donnent un paiement attendu.
 */
export function factureReglee(x: ExtractionPiece): boolean {
  if (x.modeReglement === 'deja_paye' || x.modeReglement === 'carte') return true
  if (x.modeReglement === 'prelevement') return true
  return x.montants.aPayerCts === 0 && (x.montants.ttcCts ?? 0) > 0
}

/**
 * Les effets d'une facture sur son paiement attendent-ils de savoir qui l'a réglée ? Une facture
 * reçue dans une boîte personnelle, sans règlement dit, attend la réponse à « réglée par la
 * société ou avancée ? » quand cette question est posée (`questionPosee` : entité qui transmet
 * au cabinet). Sinon (le foyer, sans cabinet), personne ne la posera : une facture déjà réglée
 * d'après sa lecture (carte, reçu) a aussitôt son paiement « payé », quel que soit son chemin
 * d'arrivée ; une facture à payer attend comme avant.
 */
export function paiementEnAttenteDuReglement(
  p: { boitePersonnelle: boolean; reglement: string | null; type: string },
  extraction: ExtractionPiece | null,
  questionPosee: boolean,
): boolean {
  if (!p.boitePersonnelle || p.reglement !== null || p.type !== 'facture') return false
  return questionPosee || !extraction || !factureReglee(extraction)
}

/**
 * Facture de référence dont un doublon (son reçu, du même mail ou du même numéro) atteste le
 * règlement : lue comme réglée, par carte si le doublon le dit.
 */
export function avecReglementDesDoublons(
  x: ExtractionPiece,
  doublons: readonly ExtractionPiece[],
): ExtractionPiece {
  if (x.type !== 'facture' || factureReglee(x)) return x
  const regle = doublons.find((d) => d.type === 'facture' && factureReglee(d))
  if (!regle) return x
  return { ...x, modeReglement: regle.modeReglement === 'carte' ? 'carte' : 'deja_paye' }
}

const numeroNormalise = (n: string | null) => n?.replace(/[^0-9A-Za-z]/g, '').toUpperCase() || null

/**
 * SIREN d'un identifiant lu : SIRET (14 chiffres) ou TVA intracommunautaire (FR + clé + SIREN)
 * ramenés aux neuf chiffres du SIREN.
 */
export function sirenDe(s: string | null): string | null {
  if (!s) return null
  const chiffres = s.replace(/\D/g, '')
  if (chiffres.length === 14) return chiffres.slice(0, 9)
  if (chiffres.length === 11 && /^\s*FR/i.test(s)) return chiffres.slice(2)
  return chiffres || null
}
const siren = sirenDe

/** Nom comparable : sans accents ni ponctuation, en majuscules. */
export const nomNormalise = (s: string | null) =>
  s
    ? normaliserLibelle(s)
        .replace(/[^A-Z0-9 ]/g, ' ')
        .trim()
    : ''

const nom = nomNormalise

/** Un nom en contient un autre, mot pour mot (« SAS FLOW DESIGN » contient « FLOW DESIGN »). */
export function contient(grand: string, petit: string): boolean {
  if (petit.length < 4) return grand === petit && grand !== ''
  return ` ${grand.replace(/\s+/g, ' ')} `.includes(` ${petit.replace(/\s+/g, ' ')} `)
}

function trouverTiers(
  contrepartie: { nom: string | null; siren: string | null; iban?: string | null },
  tiers: readonly TiersRef[],
): TiersRef | null {
  const s = siren(contrepartie.siren)
  if (s) {
    const t = tiers.find((x) => siren(x.siren) === s)
    if (t) return t
  }
  const iban = contrepartie.iban?.replace(/\s/g, '').toUpperCase()
  if (iban) {
    const t = tiers.find((x) => x.iban?.replace(/\s/g, '').toUpperCase() === iban)
    if (t) return t
  }
  const n = nom(contrepartie.nom)
  if (!n) return null
  return (
    tiers.find(
      (x) =>
        nom(x.nom) === n ||
        contient(n, nom(x.nom)) ||
        x.libellesBancaires.some((l) => contient(n, nom(l))),
    ) ?? null
  )
}

function trouverEntite(
  partie: { nom: string | null; siren: string | null },
  entites: readonly EntiteRef[],
): EntiteRef | null {
  const s = siren(partie.siren)
  if (s) {
    const e = entites.find(
      (x) =>
        siren(x.siren) === s ||
        (x.identifiants ?? []).some((i) => i.replace(/\D/g, '').length >= 9 && siren(i) === s),
    )
    if (e) return e
  }
  const n = nom(partie.nom)
  if (!n) return null
  // Par le nom ou un identifiant non numérique ; deux entités possibles : aucune (à vérifier).
  const possibles = entites.filter(
    (x) =>
      contient(n, nom(x.nom)) ||
      (x.identifiants ?? []).some((i) => i.replace(/\D/g, '').length < 9 && contient(n, nom(i))),
  )
  return possibles.length === 1 ? possibles[0]! : null
}

/**
 * Contrepartie d'une pièce : l'émetteur, ou le client d'une vente. Tiers connu (SIREN, IBAN,
 * nom) ; sinon, pour une facture, à créer d'après la pièce ; null si rien ne le nomme.
 *
 * `retenu` : un humain retient ce fournisseur (« Toujours », « Retenir pour ») ; il est créé
 * quel que soit le type de pièce (fiche de paie, avis, courrier), dès que son nom est lu.
 */
export function contrepartieDe(
  x: ExtractionPiece,
  entites: readonly EntiteRef[],
  tiers: readonly TiersRef[],
  options: { retenu?: boolean } = {},
): TiersDecision {
  const emise = factureEmise(x, entites)
  const contrepartie = emise ? { ...x.destinataire, iban: null } : x.emetteur
  const connu = trouverTiers(contrepartie, tiers)
  if (connu) return { tiersId: connu.id }
  if (!contrepartie.nom || (x.type !== 'facture' && !options.retenu)) return null
  return {
    creer: {
      nom: contrepartie.nom,
      siren: siren(contrepartie.siren),
      role: emise
        ? 'client'
        : x.type === 'avis' || x.type === 'accuse_depot'
          ? 'administration'
          : 'fournisseur',
    },
  }
}

/**
 * Tiers déjà connu du référentiel pour la contrepartie de la pièce (le fournisseur, ou le
 * client d'une vente) ; null s'il est nouveau.
 */
export function fournisseurConnu(
  x: ExtractionPiece,
  entites: readonly EntiteRef[],
  tiers: readonly TiersRef[],
): TiersRef | null {
  const emise = factureEmise(x, entites)
  return trouverTiers(emise ? { ...x.destinataire, iban: null } : x.emetteur, tiers)
}

/** Facture émise par l'une de nos entités (vente), plutôt que reçue d'un fournisseur. */
export function factureEmise(x: ExtractionPiece, entites: readonly EntiteRef[]): boolean {
  return x.type === 'facture' && (x.sens === 'emise' || trouverEntite(x.emetteur, entites) !== null)
}

/** Entité nommée par l'extraction d'après l'indication (nom exact du référentiel). */
function entiteNommee(nomIndique: string | null | undefined, entites: readonly EntiteRef[]) {
  const n = nom(nomIndique ?? null)
  return n ? (entites.find((x) => nom(x.nom) === n) ?? null) : null
}

/** Échéance ouverte de l'entité pour cet impôt et cette année. */
function echeanceImpot(
  x: ExtractionPiece,
  entiteId: string,
  echeances: readonly EcheanceRef[],
  preferer: 'passee' | 'premiere',
): EcheanceRef | null {
  if (!x.impot || !REGLES_IMPOT[x.impot]) return null
  const types = REGLES_IMPOT[x.impot]!
  const candidates = echeances
    .filter(
      (e) =>
        e.entiteId === entiteId &&
        e.regleType !== null &&
        types.includes(e.regleType) &&
        (e.statut === 'a_venir' || e.statut === 'en_preparation') &&
        (x.annee === null || e.periode.startsWith(String(x.annee))),
    )
    .sort((a, b) => a.date.localeCompare(b.date))
  if (preferer === 'passee' && x.dateDocument) {
    // Un accusé suit le dépôt : la dernière échéance déjà commencée à sa date.
    const passees = candidates.filter((e) => e.date <= x.dateDocument!)
    if (passees.length > 0) return passees.at(-1)!
  }
  return candidates[0] ?? null
}

/**
 * Échéance déjà faite de l'entité pour cet impôt et cette année, au montant de l'avis (payé
 * avant que l'avis n'arrive dans l'appli) : l'avis la documente.
 */
function echeanceFaiteAuMontant(
  x: ExtractionPiece,
  entiteId: string,
  echeances: readonly EcheanceRef[],
): EcheanceRef | null {
  const types = x.impot ? REGLES_IMPOT[x.impot] : undefined
  const montant = x.montants.aPayerCts
  if (!types || x.annee === null || montant === null) return null
  return (
    echeances.find(
      (e) =>
        e.entiteId === entiteId &&
        e.regleType !== null &&
        types.includes(e.regleType) &&
        e.statut === 'faite' &&
        e.periode.startsWith(String(x.annee)) &&
        (e.montantReelCts ?? e.montantPrevuCts) === montant,
    ) ?? null
  )
}

/** Motif d'une pièce dont seule l'entité manque : une consigne ou l'opération qui la paie la tranche. */
export const MOTIF_ENTITE_INTROUVABLE = 'Entité introuvable.'

/**
 * Décide du rattachement d'une pièce extraite. Rien d'incertain n'est appliqué : entité
 * introuvable ou archivée, confiance faible, montant illisible ou contradictoire donnent
 * « à vérifier », tranché par un humain.
 *
 * Indication du déposant : l'entité qu'elle désigne l'emporte sur l'entité par défaut du
 * tiers, et suffit pour une pièce muette (ticket de caisse) ; si la pièce en nomme une
 * autre, c'est à vérifier.
 *
 * Ordre : entité imposée, indiquée, nommée sur la pièce (SIREN, nom) ; puis le bien du
 * référentiel dont la pièce lit l'adresse ; puis les consignes du fournisseur (une consigne
 * qui mène ailleurs que le bien : à vérifier) ; puis l'entité par défaut du fournisseur.
 */
export function rattacherPiece(entree: {
  extraction: ExtractionPiece
  entites: readonly EntiteRef[]
  tiers: readonly TiersRef[]
  echeances: readonly EcheanceRef[]
  paiements: readonly PaiementRef[]
  seuilConfiance?: number
  /** Indication écrite ou dictée au dépôt. */
  indication?: string | null
  /** Entité choisie par un humain (décision du dossier d'un mail) : elle l'emporte. */
  entiteImposee?: string | null
  /** Consignes de fournisseur actives (celles du tiers de la pièce s'appliquent). */
  consignesFournisseur?: readonly ConsigneFournisseurRef[]
  /** Biens du référentiel : l'adresse lue sur la pièce désigne leur entité. */
  biens?: readonly BienRef[]
  /**
   * Texte libre jugé par Claude : absent, pas encore jugé ; null, aucune consigne retenue ;
   * sinon la consigne retenue.
   */
  consigneParTexte?: string | null
}): Rattachement {
  const x = entree.extraction
  const motifs: string[] = []

  // Facture émise : l'émetteur est l'une de nos entités.
  const emettrice = x.type === 'facture' ? trouverEntite(x.emetteur, entree.entites) : null
  const emise = x.type === 'facture' && (x.sens === 'emise' || emettrice !== null)
  const contrepartie = emise ? { ...x.destinataire, iban: null } : x.emetteur
  const tiersConnu = trouverTiers(contrepartie, entree.tiers)

  const nommee = emise ? emettrice : trouverEntite(x.destinataire, entree.entites)
  const imposee = entree.entiteImposee
    ? (entree.entites.find((e) => e.id === entree.entiteImposee) ?? null)
    : null
  const indiquee = entree.indication ? entiteNommee(x.entiteIndiquee, entree.entites) : null
  // Le motif ne nomme pas l'autre entité : une associée qui l'a déposée le lirait.
  if (!imposee && indiquee && nommee && indiquee.id !== nommee.id)
    return {
      issue: 'a_verifier',
      entiteId: indiquee.id,
      motifs: ['L’indication du dépôt et la pièce désignent deux entités différentes.'],
    }
  let entite = imposee ?? indiquee ?? nommee
  // Puis le bien dont la pièce lit l'adresse (taxe foncière, eau, énergie, travaux adressés
  // aux personnes) : le référentiel dit à quelle entité il appartient.
  const bien = entite ? null : bienDesReferences(x.references, entree.biens ?? [])
  const parBien = bien ? (entree.entites.find((e) => e.id === bien.entiteId) ?? null) : null
  // Puis les consignes du fournisseur : un fournisseur à plusieurs endroits n'a pas d'entité
  // par défaut qui vaille ; si ni une consigne ni un bien ne tranchent, un humain décide
  // (nouvel endroit).
  let consigneRetenue: ConsigneFournisseurRef | null = null
  if (!entite && tiersConnu) {
    const r = appliquerConsignesFournisseur(
      x.references ?? [],
      (entree.consignesFournisseur ?? []).filter((c) => c.tiersId === tiersConnu.id),
      entree.consigneParTexte,
    )
    const nonCouverte = `${tiersConnu.nom} : adresse ou contrat non couvert par ses consignes.`
    if (r.issue === 'contradiction')
      return {
        issue: 'a_verifier',
        entiteId: null,
        motifs: [`${tiersConnu.nom} : ses consignes désignent des entités différentes.`],
      }
    if (r.issue === 'a_juger' && !parBien)
      return {
        issue: 'a_verifier',
        entiteId: null,
        motifs: [nonCouverte],
        consignesAJuger: r.consigneIds,
      }
    if (r.issue === 'non_couverte' && !parBien)
      return { issue: 'a_verifier', entiteId: null, motifs: [nonCouverte] }
    if (r.issue === 'tranche') {
      entite = entree.entites.find((e) => e.id === r.consigne.entiteId) ?? null
      if (!entite) return { issue: 'a_verifier', entiteId: null, motifs: [nonCouverte] }
      if (parBien && parBien.id !== entite.id)
        return {
          issue: 'a_verifier',
          entiteId: null,
          motifs: [
            `${tiersConnu.nom} : sa consigne et le bien nommé sur la pièce désignent des entités différentes.`,
          ],
        }
      consigneRetenue = r.consigne
    }
  }
  const bienRetenu = !entite && parBien ? bien : null
  if (!entite && parBien) entite = parBien
  if (!entite && tiersConnu?.entiteParDefautId)
    entite = entree.entites.find((e) => e.id === tiersConnu.entiteParDefautId) ?? null

  if (!entite) return { issue: 'a_verifier', entiteId: null, motifs: [MOTIF_ENTITE_INTROUVABLE] }
  if (entite.archivee) motifs.push(`${entite.nom} est archivée : lecture seule.`)
  if (x.confiance < (entree.seuilConfiance ?? SEUIL_CONFIANCE))
    motifs.push('Extraction peu sûre : à relire.')

  const tiers = contrepartieDe(x, entree.entites, entree.tiers)

  const effets: EffetPiece[] = []
  switch (x.type) {
    case 'facture': {
      const regle = !emise && factureReglee(x)
      // Déjà réglée : le montant effectivement payé (à payer lu, sinon TTC, le reste dû étant nul).
      const montant = regle
        ? montantEffectif(x.montants)
        : (x.montants.aPayerCts ?? x.montants.ttcCts)
      if (montant === null) {
        motifs.push('Montant illisible.')
        break
      }
      // Facture à zéro (offre gratuite) : rien à payer ni à rapprocher.
      if (montant === 0) break
      // Avoir (montant négatif : remboursement, note de crédit) : l'argent revient ; attendu
      // dans l'autre sens, pour sa valeur absolue (un montant dû n'est jamais négatif).
      const avoir = montant < 0
      const du = Math.abs(montant)
      const sens: SensPaiement = emise !== avoir ? 'encaisser' : 'payer'
      const tiersId = tiersConnu?.id ?? null
      const numero = numeroNormalise(x.numero)
      // Même numéro chez le même tiers (la facture et son reçu) : un seul paiement.
      const memeNumero = numero
        ? entree.paiements.find(
            (p) =>
              p.entiteId === entite.id &&
              p.sens === sens &&
              p.statut !== 'annule' &&
              p.tiersId === tiersId &&
              numeroNormalise(p.pieceNumero ?? null) === numero,
          )
        : undefined
      if (memeNumero) {
        if (regle && PAIEMENTS_OUVERTS.includes(memeNumero.statut))
          effets.push({ type: 'acquitter_paiement', paiementId: memeNumero.id })
        break
      }
      const attendu = entree.paiements.find(
        (p) =>
          p.entiteId === entite.id &&
          p.sens === sens &&
          PAIEMENTS_OUVERTS.includes(p.statut) &&
          p.pieceId === null &&
          p.montantDuCts === du &&
          (p.tiersId === null || p.tiersId === tiersId),
      )
      if (attendu)
        effets.push({
          type: regle ? 'acquitter_paiement' : 'lier_paiement',
          paiementId: attendu.id,
        })
      else
        effets.push({
          type: 'creer_paiement',
          sens,
          montantDuCts: du,
          devise: x.devise ?? 'EUR',
          datePrevue: x.dateEcheance ?? x.dateDocument,
          moyen: emise ? 'facture_client' : MOYEN_DU_REGLEMENT[x.modeReglement],
          libelle: x.objet ?? (x.numero ? `${avoir ? 'Avoir' : 'Facture'} ${x.numero}` : null),
          dejaRegle: regle,
        })
      break
    }
    case 'avis': {
      const e = echeanceImpot(x, entite.id, entree.echeances, 'premiere')
      if (!e) {
        // Échéance de l'année déjà faite, au montant de l'avis : il la documente.
        const faite = echeanceFaiteAuMontant(x, entite.id, entree.echeances)
        if (faite) {
          effets.push({ type: 'joindre_echeance', echeanceId: faite.id })
          break
        }
        // Rien à payer, ou prélevé d'office (mensualisation, prélèvement à l'échéance) : la
        // banque le montrera, personne n'a rien à faire.
        if (
          x.montants.aPayerCts === 0 ||
          x.modeReglement === 'prelevement' ||
          x.modeReglement === 'deja_paye'
        )
          break
        motifs.push('Aucune échéance ouverte pour cet avis.')
        break
      }
      if (x.montants.aPayerCts === null) {
        motifs.push('Montant de l’avis illisible.')
        break
      }
      if (e.montantPrevuCts !== null && e.montantPrevuCts !== x.montants.aPayerCts) {
        motifs.push('Le montant de l’avis diffère de celui déjà saisi.')
        break
      }
      effets.push({ type: 'fixer_montant', echeanceId: e.id, montantCts: x.montants.aPayerCts })
      break
    }
    case 'accuse_depot': {
      const e = echeanceImpot(x, entite.id, entree.echeances, 'passee')
      if (e) effets.push({ type: 'preuve', echeanceId: e.id })
      else motifs.push('Aucune échéance ouverte pour cet accusé.')
      break
    }
    case 'courrier':
      // Un courrier à simplement archiver (attestation, information) ne crée pas de tâche ;
      // dans le doute, si.
      if (x.actionRequise !== false)
        effets.push({
          type: 'creer_tache',
          titre: `Traiter le courrier : ${x.objet ?? 'sans objet lisible'}`,
          dateDue: x.dateEcheance,
        })
      break
    default:
      // Relevé, contrat, fiche de paie, autre : classés seulement, sauf action demandée par le document.
      if (x.actionRequise === true)
        effets.push({
          type: 'creer_tache',
          titre: `Traiter le document : ${x.objet ?? 'sans objet lisible'}`,
          dateDue: x.dateEcheance,
        })
      break
  }

  // Entité imposée par un humain qui a vu la pièce : ses doutes de lecture sont levés.
  if (motifs.length > 0 && !imposee) return { issue: 'a_verifier', entiteId: entite.id, motifs }
  return {
    issue: 'rattachee',
    entiteId: entite.id,
    tiers,
    effets,
    ...(consigneRetenue && { consigneFournisseurId: consigneRetenue.id }),
    ...(bienRetenu && { bienId: bienRetenu.id }),
  }
}

// Dépense IA

export type UsageIa = {
  entree: number
  sortie: number
  cacheEcriture: number
  cacheLecture: number
}

/** Tarifs en dollars par million de jetons (entrée, sortie). */
const TARIFS: Record<string, { entree: number; sortie: number }> = {
  'claude-haiku-4-5': { entree: 1, sortie: 5 },
  'claude-sonnet-5-5': { entree: 2, sortie: 10 },
  'claude-opus-5-5': { entree: 4, sortie: 20 },
}

/**
 * Coût d'un appel en micro-dollars (entier) : l'écriture en cache coûte 1,25 fois
 * l'entrée, la lecture 0,1 fois. Null pour un modèle dont le tarif n'est pas connu.
 */
export function coutIa(modele: string, u: UsageIa): number | null {
  const t = TARIFS[modele]
  if (!t) return null
  const centiemes =
    u.entree * t.entree * 100 +
    u.sortie * t.sortie * 100 +
    u.cacheEcriture * t.entree * 125 +
    u.cacheLecture * t.entree * 10
  return Math.round(centiemes / 100)
}

// Schémas d'entrée des routes

/** Indication du déposant : où va la pièce (« perso », « Flow Design, repas client »). */
export const indicationDepot = z
  .string()
  .max(500)
  .transform((t) => t.trim() || undefined)
  .optional()

/** Dépôt d'une pièce depuis l'interface (ou l'assistant) : fichier en base64. */
export const pieceDepot = z.object({
  nomFichier: z.string().trim().min(1).max(200),
  contenuBase64: z.string().min(1).max(20_000_000),
  entiteId: z.uuid().optional(),
  /** La tâche où la pièce est glissée : elle rejoint son mail, ou la tâche libre elle-même. */
  tacheId: z.uuid().optional(),
  indication: indicationDepot,
})

/** Formats d'enregistrement des navigateurs (MediaRecorder) acceptés pour la dictée. */
export const FORMATS_AUDIO = [
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
  'audio/mpeg',
  'audio/wav',
] as const

/** Dictée de l'indication : un enregistrement court (une minute, 750 Ko au plus), jamais gardé. */
export const transcriptionDepot = z.object({
  audioBase64: z.string().min(1).max(1_000_000),
  typeMime: z
    .string()
    .transform((t) => t.split(';')[0]!.trim().toLowerCase())
    .pipe(z.enum(FORMATS_AUDIO)),
  /** Ce que la dictée remplit : l'indication d'une pièce (défaut) ou une tâche, plus longue. */
  pour: z.enum(['indication', 'tache']).optional(),
})

const exerciceSaisi = z.number().int().min(2000).max(2100).nullable()

/** Correction humaine du rattachement : entité, tiers, type ; puis les effets s'appliquent. */
export const pieceRattachement = z.object({
  entiteId: z.uuid(),
  type: z.enum(TYPES_PIECE).optional(),
  /** Rubrique du plan de classement ; à défaut, celle reconnue à la lecture. */
  rubrique: z.enum(RUBRIQUES).optional(),
  exerciceConcerne: exerciceSaisi.optional(),
  emetteurId: z.uuid().nullable().optional(),
  echeanceId: z.uuid().nullable().optional(),
  paiementId: z.uuid().nullable().optional(),
  /**
   * Retenir l'entité pour ce fournisseur (un seul endroit) : elle devient son entité par
   * défaut, ses prochaines pièces s'y rattachent seules.
   */
  retenirFournisseur: z.boolean().optional(),
  /**
   * Retenir pour ce fournisseur à plusieurs endroits : une consigne (référence lue, texte) qui
   * mène ses prochaines pièces à cette entité. Exclut « retenirFournisseur ».
   */
  consigneFournisseur: consigneDepuisPiece.optional(),
  /** Ce que dit l'humain de la pièce (écrit ou dicté) : lu par Claude pour la placer dans le plan. */
  indication: z.string().trim().min(1).max(500).optional(),
})

/**
 * Classement choisi par un humain : le dossier du plan de l'entité (ou, sans racine de
 * classement, la rubrique des anciens chemins), et l'exercice concerné quand ce n'est pas
 * celui de la date du document. Une pièce déjà rangée suit dans le Drive.
 */
export const pieceClassement = z
  .object({
    dossierId: z.uuid().optional(),
    rubrique: z.enum(RUBRIQUES).optional(),
    exerciceConcerne: exerciceSaisi.optional(),
  })
  .refine((c) => (c.dossierId === undefined) !== (c.rubrique === undefined), {
    error: 'Choisir un dossier du plan (ou, sans plan, une rubrique).',
  })

/**
 * Facture reçue dans une boîte personnelle : réglée par la société (elle part au cabinet), ou
 * avancée par une personne (note de frais à créer dans Tiime, par défaut la propriétaire de
 * la boîte).
 */
export const pieceReglement = z.object({
  reglement: z.enum(REGLEMENTS_PIECE),
  personneId: z.uuid().optional(),
})

/**
 * Envoi au cabinet interrompu : la pièce est bien arrivée dans Tiime (vérifié par un humain),
 * ou elle est à renvoyer ; « reessayer » relance tout de suite une destination en échec.
 */
export const pieceAcheminement = z.object({
  action: z.enum(['arrivee', 'renvoyer', 'reessayer', 'deposee_a_la_main']),
})

const sirenSaisi = z
  .string()
  .transform((s) => s.replace(/\s/g, ''))
  .pipe(z.string().regex(/^\d{9}$/, 'SIREN : neuf chiffres.'))
const texteSaisi = (max: number) => z.string().trim().min(1).max(max)

/**
 * Correction à la main de ce qui a été lu sur la pièce (montant mal lu, émetteur tronqué) :
 * seuls les champs donnés changent ; null vide un champ.
 */
export const pieceCorrection = z
  .object({
    emetteurNom: texteSaisi(200).nullable(),
    emetteurSiren: sirenSaisi.nullable(),
    destinataireNom: texteSaisi(200).nullable(),
    destinataireSiren: sirenSaisi.nullable(),
    numero: texteSaisi(100).nullable(),
    dateDocument: z.iso.date().nullable(),
    dateEcheance: z.iso.date().nullable(),
    htCts: z.number().int().nullable(),
    tvaCts: z.number().int().nullable(),
    ttcCts: z.number().int().nullable(),
    aPayerCts: z.number().int().nullable(),
    objet: texteSaisi(300).nullable(),
    modeReglement: z.enum(MODES_REGLEMENT),
  })
  .partial()

/** Montant que la pièce demande de régler : le reste à payer, sinon le TTC. */
export function montantDuPiece(x: ExtractionPiece | null): number | null {
  return x ? (x.montants.aPayerCts ?? x.montants.ttcCts) : null
}

/**
 * Applique une correction à la lecture d'une pièce. Le type est celui de la pièce (un humain
 * a pu le changer au rattachement). Sans lecture (extraction impossible), on part d'une
 * lecture vide. Le résultat est marqué « corrigée ».
 */
export function corrigerExtraction(
  x: ExtractionPiece | null,
  type: TypePiece,
  c: PieceCorrection,
): ExtractionPiece {
  const depart: ExtractionPiece = x ?? {
    type,
    sens: null,
    emetteur: { nom: null, siren: null, iban: null },
    destinataire: { nom: null, siren: null },
    numero: null,
    dateDocument: null,
    dateEcheance: null,
    montants: { htCts: null, tvaCts: null, ttcCts: null, aPayerCts: null },
    devise: 'EUR',
    objet: null,
    impot: null,
    annee: null,
    modeReglement: 'inconnu',
    confiance: 0,
  }
  const garder = <T>(nouveau: T | undefined, ancien: T): T =>
    nouveau === undefined ? ancien : nouveau
  return {
    ...depart,
    type,
    emetteur: {
      ...depart.emetteur,
      nom: garder(c.emetteurNom, depart.emetteur.nom),
      siren: garder(c.emetteurSiren, depart.emetteur.siren),
    },
    destinataire: {
      nom: garder(c.destinataireNom, depart.destinataire.nom),
      siren: garder(c.destinataireSiren, depart.destinataire.siren),
    },
    numero: garder(c.numero, depart.numero),
    dateDocument: garder(c.dateDocument, depart.dateDocument),
    dateEcheance: garder(c.dateEcheance, depart.dateEcheance),
    montants: {
      htCts: garder(c.htCts, depart.montants.htCts),
      tvaCts: garder(c.tvaCts, depart.montants.tvaCts),
      ttcCts: garder(c.ttcCts, depart.montants.ttcCts),
      aPayerCts: garder(c.aPayerCts, depart.montants.aPayerCts),
    },
    objet: garder(c.objet, depart.objet),
    modeReglement: garder(c.modeReglement, depart.modeReglement),
    corrigee: true,
  }
}

export type PieceDepot = z.infer<typeof pieceDepot>
export type TranscriptionDepot = z.infer<typeof transcriptionDepot>
export type PieceCorrection = z.infer<typeof pieceCorrection>
export type PieceRattachement = z.infer<typeof pieceRattachement>
export type PieceClassement = z.infer<typeof pieceClassement>
export type PieceReglement = z.infer<typeof pieceReglement>
export type PieceAcheminement = z.infer<typeof pieceAcheminement>
